import { Button, Input } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import OWButton from '../../../components/ow_button/OWButton';
import { AgentActionOutcome, AgentActionState } from '../../../types/agentHarness';
import { cn } from '../../../utils/ClassNamesUtils';
import { useOscarTheme } from '../../context/OscarRoleContext';
import { humanizeToolName } from '../../utils/OscarLabels';

interface OscarActionReviewCardProps {
	toolName: string;
	createdAt: string;
	state: AgentActionState;
	/** Resolves false when the verification did not reach the harness, so the card unlocks. */
	onReconcile?: (outcome: AgentActionOutcome, evidence: string) => void | Promise<boolean>;
	className?: string;
}

const EVIDENCE_MAX_CHARS = 2000;

/**
 * A write whose result was lost. The harness pauses every later call of that
 * tool for this login until the user checks the record and says what happened;
 * this card is where they say it. Answering records the verification only — it
 * never runs or re-runs the write.
 */
export const OscarActionReviewCard = ({
	toolName,
	createdAt,
	state,
	onReconcile,
	className,
}: OscarActionReviewCardProps) => {
	const theme = useOscarTheme();
	const [evidence, setEvidence] = useState('');
	const [deciding, setDeciding] = useState<AgentActionOutcome | null>(null);
	const started = createdAt ? new Date(createdAt).toLocaleString() : '';
	const tool = humanizeToolName(toolName);

	const decide = (outcome: AgentActionOutcome) => {
		if (!onReconcile || deciding || !evidence.trim()) {
			return;
		}
		setDeciding(outcome);
		Promise.resolve(onReconcile(outcome, evidence.trim()))
			.then((landed) => {
				if (landed === false) {
					setDeciding(null);
				}
			})
			.catch(() => setDeciding(null));
	};

	if (state !== 'outcome_unknown') {
		return (
			<div
				className={cn(
					'flex w-full flex-col gap-2xs rounded p-xs',
					theme.surface,
					theme.stroke,
					className
				)}
			>
				<span className="bodymedium text-grey-10">{`${tool} — outcome verified`}</span>
				<span className="footnoteregular text-character-secondary">
					{state === 'confirmed_applied'
						? 'You confirmed it went through. Oscar can use this action again.'
						: 'You confirmed it did not go through. Oscar can use this action again.'}
				</span>
			</div>
		);
	}

	return (
		<div
			className={cn(
				'flex w-full flex-col gap-xs rounded p-xs',
				theme.surface,
				theme.stroke,
				className
			)}
		>
			<span className="bodymedium text-grey-10">Check what happened</span>
			<span className="bodyregular text-grey-9" title={toolName}>
				<span className="bodymedium">{tool}</span>
				{started ? ` (started ${started})` : ''}
				{
					' may or may not have gone through. Oscar will not run it again until you check the record and tell it what happened. This does not run anything.'
				}
			</span>
			<Input.TextArea
				aria-label="How you checked"
				placeholder="How did you check? e.g. searched work orders at the location — none created"
				value={evidence}
				maxLength={EVIDENCE_MAX_CHARS}
				autoSize={{ minRows: 2, maxRows: 4 }}
				disabled={!!deciding}
				onChange={(event) => setEvidence(event.target.value)}
			/>
			<div className="flex flex-wrap items-center justify-end gap-xs">
				<Button
					size="large"
					loading={deciding === 'confirmed_applied'}
					disabled={!evidence.trim() || (!!deciding && deciding !== 'confirmed_applied')}
					onClick={() => decide('confirmed_applied')}
				>
					It went through
				</Button>
				<OWButton
					size="large"
					type="primary"
					loading={deciding === 'confirmed_not_applied'}
					disabled={!evidence.trim() || (!!deciding && deciding !== 'confirmed_not_applied')}
					onClick={() => decide('confirmed_not_applied')}
				>
					It did not go through
				</OWButton>
			</div>
		</div>
	);
};

export default OscarActionReviewCard;
